/**
 * Sidebar panels for the Visual Editor — one component per selection kind.
 * Panels mutate the canvas through the EditorBridge and report every change
 * via markDirty so the shell tracks dirty fields + builds the save summary.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Slider,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  ArrowUp as ArrowUpwardIcon,
  ArrowDown as ArrowDownwardIcon,
  Copy as ContentCopyIcon,
  Trash as DeleteOutlineIcon,
  PencilSimple as EditIcon,
  Lock as LockIcon,
} from '@phosphor-icons/react';

import { ColorPicker } from '../../../../components/cms/ColorPicker';
import type {
  EditorDocumentResponse,
  EditorFieldKey,
  EditorRegion,
  EditorSelection,
  EditorStyleVocab,
  WebsiteComponentOptions,
} from '../../../../types/websiteBuilder';
import type { EditorBridge } from './editorBridge';
import {
  composeGradient,
  isTelHref,
  isWideScope,
  parseGradient,
  scopeLabel,
  swapButtonClass,
  swapSectionClass,
} from './visualEditorLogic';

export interface PanelContext {
  selection: EditorSelection;
  /** The stored region this selection lives in, if any. */
  region?: EditorRegion;
  bridge: EditorBridge;
  vocab: EditorStyleVocab;
  /** Organization brand colours, site palette, and typefaces. */
  design: EditorDocumentResponse['design'];
  pages: Array<{ slug: string; title: string }>;
  options?: WebsiteComponentOptions;
  markDirty: (fieldKey: EditorFieldKey | undefined, opLabel: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateComponentConfig: (patch: Record<string, any>) => void;
  addRemovedMarkers: (markers: string[]) => void;
  clearSelection: () => void;
}

/** ColorPicker shows its "Brand Colors" section only for these named slots. */
const BRAND_SLOTS = ['primary', 'secondary', 'tertiary', 'quaternary', 'quinary'] as const;

function brandSlots(colors: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  BRAND_SLOTS.forEach((slot, index) => {
    if (colors[index]) out[slot] = colors[index];
  });
  return out;
}

/** A row of one-click swatches — the site's own palette, which the generated
 * design actually uses, alongside the brand colours. */
function SwatchRow({
  label,
  colors,
  active,
  onPick,
}: {
  label: string;
  colors: string[];
  active?: string;
  onPick: (color: string) => void;
}) {
  if (!colors.length) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <FormLabel sx={{ mb: 0.5 }}>{label}</FormLabel>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {colors.map((color) => (
          <Box
            key={color}
            onClick={() => onPick(color)}
            title={color}
            sx={{
              width: 26,
              height: 26,
              borderRadius: 'sm',
              cursor: 'pointer',
              bgcolor: color,
              border: '2px solid',
              borderColor: active?.toLowerCase() === color.toLowerCase() ? 'primary.500' : 'neutral.outlinedBorder',
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

const SPACING_PRESETS: Array<{ label: string; value: string }> = [
  { label: 'Compact', value: '24px 16px' },
  { label: 'Normal', value: '' },
  { label: 'Spacious', value: '96px 24px' },
];

/** Tells the user how far an edit here reaches, before they make one.
 * Deliberately quiet for page-scope edits — most layouts are used by a
 * single page, and a warning there would just be noise. */
export function ScopeBanner({ region }: { region?: EditorRegion }) {
  if (!region) return null;
  if (!isWideScope(region)) {
    return (
      <Typography level="body-xs" sx={{ mb: 1 }}>
        {scopeLabel(region)}
      </Typography>
    );
  }
  return (
    <Alert
      color={region.scope === 'site' ? 'primary' : 'warning'}
      size="sm"
      variant="soft"
      sx={{ mb: 1 }}
    >
      <Box>
        <Typography level="title-sm">{scopeLabel(region)}</Typography>
        <Typography level="body-xs">
          {region.scope === 'site'
            ? 'This is your header and footer — changes appear on every page of your site.'
            : 'These pages share this layout, so your change appears on all of them.'}
        </Typography>
      </Box>
    </Alert>
  );
}

function PanelTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography level="title-md" sx={{ mb: 1 }}>
      {children}
    </Typography>
  );
}

// ---------------------------------------------------------------------------

export function TextPanel({ ctx }: { ctx: PanelContext }) {
  const { selection, bridge, markDirty } = ctx;
  const hasChildren = !!bridge.selectedElement?.children.length;
  const [text, setText] = useState(selection.meta.text ?? '');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => setText(selection.meta.text ?? ''), [selection]);

  const commit = (value: string) => {
    if (value === (selection.meta.text ?? '')) return;
    bridge.setText(value);
    markDirty(selection.fieldKey, 'Edited text');
  };

  return (
    <Box>
      <PanelTitle>Text</PanelTitle>
      <ScopeBanner region={ctx.region} />
      <Typography level="body-xs" sx={{ mb: 1 }}>
        {`<${selection.meta.tag}>`} in {selection.fieldKey || 'this page'}
      </Typography>
      {hasChildren ? (
        <Alert color="neutral" size="sm" sx={{ mb: 1 }}>
          This text contains links or icons — edit it directly on the page to keep them.
        </Alert>
      ) : (
        <>
        <FormControl sx={{ mb: 1 }}>
          <FormLabel
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
          >
            <span>Content</span>
            <Button size="sm" variant="plain" sx={{ minHeight: 0, py: 0 }} onClick={() => setExpanded(true)}>
              Expand
            </Button>
          </FormLabel>
          {/* Fixed, scrollable box: long copy stays fully reachable in the
              narrow sidebar, and Expand opens a full-size editor. */}
          <Textarea
            minRows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => commit(text)}
            slotProps={{
              textarea: { style: { maxHeight: '30vh', overflowY: 'auto', resize: 'vertical' } },
            }}
          />
          <Typography level="body-xs" sx={{ mt: 0.25 }}>
            {`${text.length} characters`}
          </Typography>
        </FormControl>
        <Modal open={expanded} onClose={() => setExpanded(false)}>
          <ModalDialog sx={{ width: 'min(760px, 92vw)' }}>
            <ModalClose />
            <Typography level="title-md" sx={{ mb: 1 }}>
              Edit text
            </Typography>
            <Textarea
              autoFocus
              minRows={16}
              value={text}
              onChange={(e) => setText(e.target.value)}
              slotProps={{ textarea: { style: { maxHeight: '60vh', overflowY: 'auto' } } }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button variant="plain" color="neutral" onClick={() => { setText(selection.meta.text ?? ''); setExpanded(false); }}>
                Cancel
              </Button>
              <Button onClick={() => { commit(text); setExpanded(false); }}>Apply</Button>
            </Stack>
          </ModalDialog>
        </Modal>
        </>
      )}
      <TypefaceControl ctx={ctx} />
      <Button
        size="sm"
        variant="outlined"
        startDecorator={<EditIcon />}
        onClick={() => bridge.beginInlineEdit()}
      >
        Edit inline on the page
      </Button>
      <Typography level="body-xs" sx={{ mt: 1 }}>
        Tip: click into the text on the page and type. Paste is always plain text.
      </Typography>
    </Box>
  );
}

/** Typeface picker offering the site's design fonts and the organization's
 * brand typefaces — never a generic font list, so text stays on-brand. */
export function TypefaceControl({ ctx }: { ctx: PanelContext }) {
  const { selection, bridge, design, markDirty } = ctx;
  const current = /font-family:\s*([^;]+)/.exec(selection.meta.inlineStyle)?.[1]?.replace(/['"]/g, '').trim();

  const options: Array<{ label: string; value: string }> = [];
  const add = (label: string, family: string) => {
    if (family && !options.some((o) => o.value.toLowerCase() === family.toLowerCase())) {
      options.push({ label, value: family });
    }
  };
  add(`Headings — ${design.typography.headings_font}`, design.typography.headings_font);
  add(`Body — ${design.typography.body_font}`, design.typography.body_font);
  design.brand_typefaces.forEach((family) => add(`Brand — ${family}`, family));
  if (!options.length) return null;

  return (
    <FormControl sx={{ mb: 1 }}>
      <FormLabel>Typeface</FormLabel>
      <Select
        size="sm"
        value={options.find((o) => o.value === current)?.value ?? '__default'}
        onChange={(_, value) => {
          if (!value) return;
          bridge.setStyleProperty('font-family', value === '__default' ? '' : `${value}, sans-serif`);
          markDirty(selection.fieldKey, 'Changed a typeface');
        }}
      >
        <Option value="__default">Default for this element</Option>
        {options.map((option) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    </FormControl>
  );
}

// ---------------------------------------------------------------------------

export function HeroFieldPanel({ ctx }: { ctx: PanelContext }) {
  const { selection } = ctx;
  const label =
    selection.fieldKey === 'hero_subheading'
      ? 'Hero subheading'
      : selection.fieldKey === 'breadcrumb'
        ? 'Breadcrumb'
        : 'Hero heading';
  return (
    <Box>
      <PanelTitle>{label}</PanelTitle>
      <TextPanel ctx={ctx} />
    </Box>
  );
}

// ---------------------------------------------------------------------------

export function ImagePanel({ ctx }: { ctx: PanelContext }) {
  const { selection, bridge, options, markDirty, updateComponentConfig } = ctx;
  const [alt, setAlt] = useState(selection.meta.alt ?? '');

  useEffect(() => setAlt(selection.meta.alt ?? ''), [selection]);

  const pick = (url: string, photoAlt: string) => {
    bridge.swapImage(url, alt || photoAlt);
    if (selection.slot) {
      updateComponentConfig({ images: { [selection.slot]: { url, alt_text: alt || photoAlt } } });
    }
    markDirty(selection.fieldKey, 'Swapped an image');
  };

  return (
    <Box>
      <PanelTitle>Image</PanelTitle>
      <ScopeBanner region={ctx.region} />
      {selection.slot && (
        <Typography level="body-xs" sx={{ mb: 1 }}>
          Slot: {selection.slot} — your choice is pinned and survives regeneration.
        </Typography>
      )}
      <FormControl sx={{ mb: 1 }}>
        <FormLabel>Alt text (for accessibility & SEO)</FormLabel>
        <Input
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          onBlur={() => {
            if (alt !== (selection.meta.alt ?? '')) {
              bridge.setAttr('alt', alt);
              if (selection.slot && selection.meta.src) {
                updateComponentConfig({ images: { [selection.slot]: { url: selection.meta.src, alt_text: alt } } });
              }
              markDirty(selection.fieldKey, 'Updated image alt text');
            }
          }}
        />
      </FormControl>
      <FormLabel sx={{ mb: 0.5 }}>Swap from your photo library</FormLabel>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 0.5,
          maxHeight: 320,
          overflowY: 'auto',
        }}
      >
        {(options?.photos ?? []).map((photo) => (
          <Box
            key={photo.url}
            component="img"
            src={photo.thumbnail_url || photo.url}
            alt={photo.alt_text || photo.name}
            onClick={() => pick(photo.url, photo.alt_text || photo.name)}
            sx={{
              width: '100%',
              aspectRatio: '1',
              objectFit: 'cover',
              borderRadius: 'sm',
              cursor: 'pointer',
              border: selection.meta.src === photo.url ? '2px solid' : '2px solid transparent',
              borderColor: selection.meta.src === photo.url ? 'primary.500' : 'transparent',
            }}
          />
        ))}
      </Box>
      {!options?.photos?.length && (
        <Typography level="body-sm">
          No marketing photos yet — add some in the Media Library and they'll show up here.
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------

export function ButtonPanel({ ctx }: { ctx: PanelContext }) {
  const { selection, bridge, vocab, pages, markDirty } = ctx;
  const telLocked = isTelHref(selection.meta.href);
  const hasChildren = !!bridge.selectedElement?.children.length;
  const [label, setLabel] = useState(selection.meta.text ?? '');
  const [href, setHref] = useState(selection.meta.href ?? '');

  useEffect(() => {
    setLabel(selection.meta.text ?? '');
    setHref(selection.meta.href ?? '');
  }, [selection]);

  const activeVariant = selection.meta.classes.find((c) => vocab.button_classes.includes(c));

  return (
    <Box>
      <PanelTitle>Button / link</PanelTitle>
      <ScopeBanner region={ctx.region} />
      {telLocked && (
        <Alert color="warning" size="sm" startDecorator={<LockIcon />} sx={{ mb: 1 }}>
          Phone links follow your Business Info phone number — change it in Global Settings.
        </Alert>
      )}
      {!hasChildren && (
        <FormControl sx={{ mb: 1 }}>
          <FormLabel>Label</FormLabel>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => {
              if (label !== (selection.meta.text ?? '')) {
                bridge.setText(label);
                markDirty(selection.fieldKey, 'Edited a button label');
              }
            }}
          />
        </FormControl>
      )}
      {!telLocked && (
        <FormControl sx={{ mb: 1 }}>
          <FormLabel>Links to</FormLabel>
          <Select
            value={pages.some((p) => `/${p.slug}/` === href || (!p.slug && href === '/')) ? href : '__custom'}
            onChange={(_, value) => {
              if (value && value !== '__custom') {
                setHref(value);
                bridge.setAttr('href', value);
                markDirty(selection.fieldKey, 'Changed a button link');
              }
            }}
          >
            {pages.map((p) => (
              <Option key={p.slug || 'home'} value={p.slug ? `/${p.slug}/` : '/'}>
                {p.title || p.slug || 'Homepage'}
              </Option>
            ))}
            <Option value="__custom">Custom URL…</Option>
          </Select>
          <Input
            sx={{ mt: 0.5 }}
            placeholder="https://… or /page/"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            onBlur={() => {
              if (href !== (selection.meta.href ?? '')) {
                bridge.setAttr('href', href);
                markDirty(selection.fieldKey, 'Changed a button link');
              }
            }}
          />
        </FormControl>
      )}
      {vocab.button_classes.length > 0 && (
        <FormControl>
          <FormLabel>Style</FormLabel>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {vocab.button_classes.map((cls) => (
              <Chip
                key={cls}
                size="sm"
                variant={activeVariant === cls ? 'solid' : 'outlined'}
                onClick={() => {
                  bridge.setClasses(swapButtonClass(selection.meta.classes, vocab, cls));
                  markDirty(selection.fieldKey, 'Changed a button style');
                }}
              >
                {cls.replace(/^btn-?/, '') || 'default'}
              </Chip>
            ))}
          </Stack>
        </FormControl>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------

export function SectionPanel({ ctx }: { ctx: PanelContext }) {
  const { selection, bridge, vocab, design, markDirty, addRemovedMarkers, clearSelection } = ctx;
  const brandColors = brandSlots(design.brand_colors);
  // A restyle of a dynamic (marker-driven) background displaces that binding;
  // report the displaced marker(s) so the save sanctions their removal.
  const setStyle = (prop: string, value: string) => {
    const displaced = bridge.setStyleProperty(prop, value);
    if (displaced.length) addRemovedMarkers(displaced);
  };
  const currentBackground = /background(?:-image)?:\s*([^;]+)/.exec(selection.meta.inlineStyle)?.[1] ?? '';
  // The selection carries a SNAPSHOT of the element's style taken when it was
  // clicked. Deriving the gradient controls from it means applying a gradient
  // never reveals its own editor. Track it as live state instead.
  const [gradient, setGradient] = useState(() => parseGradient(currentBackground));
  // Seeds for the "Add gradient" action — NOT displayed as current state.
  const seedFrom = design.brand_colors[0] || design.colors[0] || '#1a5f4a';
  const seedTo = design.brand_colors[1] || design.colors[1] || '#0f3d2e';
  const [from, setFrom] = useState(gradient?.from ?? seedFrom);
  const [to, setTo] = useState(gradient?.to ?? seedTo);
  const [angle, setAngle] = useState(gradient?.angleDeg ?? 135);

  // Re-sync every control when a DIFFERENT element is selected.
  useEffect(() => {
    const next = parseGradient(currentBackground);
    setGradient(next);
    setFrom(next?.from ?? seedFrom);
    setTo(next?.to ?? seedTo);
    setAngle(next?.angleDeg ?? 135);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  const activeSection = selection.meta.classes.find((c) => vocab.section_classes.includes(c));
  const dirtyLabel = 'Restyled a section';

  const applyGradient = (f: string, t: string, a: number) => {
    setStyle('background', composeGradient(f, t, a));
    setGradient({ from: f, to: t, angleDeg: a });
    markDirty(selection.fieldKey, dirtyLabel);
  };

  return (
    <Box>
      <PanelTitle>Section</PanelTitle>
      <ScopeBanner region={ctx.region} />
      {vocab.section_classes.length > 0 && (
        <FormControl sx={{ mb: 1 }}>
          <FormLabel>Background preset</FormLabel>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {vocab.section_classes.map((cls) => (
              <Chip
                key={cls}
                size="sm"
                variant={activeSection === cls ? 'solid' : 'outlined'}
                onClick={() => {
                  bridge.setClasses(swapSectionClass(selection.meta.classes, vocab, cls));
                  setStyle('background', '');
                  setGradient(null);
                  markDirty(selection.fieldKey, dirtyLabel);
                }}
              >
                {cls}
              </Chip>
            ))}
          </Stack>
        </FormControl>
      )}
      <SwatchRow
        label="Brand colors"
        colors={design.brand_colors}
        active={gradient ? undefined : currentBackground}
        onPick={(color) => {
          setStyle('background', color);
          setGradient(null);
          markDirty(selection.fieldKey, dirtyLabel);
        }}
      />
      <SwatchRow
        label="Site palette"
        colors={design.colors}
        active={gradient ? undefined : currentBackground}
        onPick={(color) => {
          setStyle('background', color);
          setGradient(null);
          markDirty(selection.fieldKey, dirtyLabel);
        }}
      />
      <FormControl sx={{ mb: 1 }}>
        <FormLabel>Custom background color</FormLabel>
        <ColorPicker
          value={gradient ? '' : currentBackground}
          helpText={currentBackground ? undefined : 'Empty — this section uses the preset above'}
          brandColors={brandColors}
          onChange={(color) => {
            setStyle('background', color);
            setGradient(null);
            markDirty(selection.fieldKey, dirtyLabel);
          }}
        />
      </FormControl>
      <FormControl sx={{ mb: 1 }}>
        <FormLabel>Gradient</FormLabel>
        {gradient ? (
          <Stack spacing={1}>
            <Box
              sx={{
                height: 28,
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'divider',
                background: composeGradient(from, to, angle),
              }}
            />
            <ColorPicker
              label="From"
              value={from}
              brandColors={brandColors}
              onChange={(c) => {
                setFrom(c);
                applyGradient(c, to, angle);
              }}
            />
            <ColorPicker
              label="To"
              value={to}
              brandColors={brandColors}
              onChange={(c) => {
                setTo(c);
                applyGradient(from, c, angle);
              }}
            />
            <Box>
              <FormLabel sx={{ fontSize: 'xs' }}>{`Angle: ${angle}\u00b0`}</FormLabel>
              <Slider
                size="sm"
                min={0}
                max={360}
                value={angle}
                onChange={(_, value) => {
                  const a = value as number;
                  setAngle(a);
                  applyGradient(from, to, a);
                }}
              />
            </Box>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => {
                setStyle('background', '');
                setGradient(null);
                markDirty(selection.fieldKey, dirtyLabel);
              }}
            >
              Remove gradient
            </Button>
          </Stack>
        ) : (
          <Button
            size="sm"
            variant="outlined"
            onClick={() => {
              setFrom(seedFrom);
              setTo(seedTo);
              setAngle(135);
              applyGradient(seedFrom, seedTo, 135);
            }}
          >
            Add a gradient
          </Button>
        )}
      </FormControl>
      <FormControl sx={{ mb: 1 }}>
        <FormLabel>Spacing</FormLabel>
        <Stack direction="row" spacing={0.5}>
          {SPACING_PRESETS.map((preset) => (
            <Chip
              key={preset.label}
              size="sm"
              variant="outlined"
              onClick={() => {
                setStyle('padding', preset.value);
                markDirty(selection.fieldKey, dirtyLabel);
              }}
            >
              {preset.label}
            </Chip>
          ))}
        </Stack>
      </FormControl>
      <Divider sx={{ my: 1 }} />
      <Stack direction="row" spacing={0.5}>
        <IconButton
          size="sm"
          variant="outlined"
          title="Move up"
          onClick={() => {
            bridge.moveSection(-1);
            markDirty(selection.fieldKey, 'Moved a section');
          }}
        >
          <ArrowUpwardIcon />
        </IconButton>
        <IconButton
          size="sm"
          variant="outlined"
          title="Move down"
          onClick={() => {
            bridge.moveSection(1);
            markDirty(selection.fieldKey, 'Moved a section');
          }}
        >
          <ArrowDownwardIcon />
        </IconButton>
        <IconButton
          size="sm"
          variant="outlined"
          title="Duplicate (embedded components are not duplicated)"
          onClick={() => {
            bridge.duplicateSection();
            markDirty(selection.fieldKey, 'Duplicated a section');
          }}
        >
          <ContentCopyIcon />
        </IconButton>
        <IconButton
          size="sm"
          variant="outlined"
          color="danger"
          title="Delete section"
          onClick={() => {
            if (!window.confirm('Delete this section? Embedded components in it are removed from the page too.')) return;
            addRemovedMarkers(bridge.deleteSelected());
            markDirty(selection.fieldKey, 'Deleted a section');
            clearSelection();
          }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------

const LOCKED_LABELS: Record<string, string> = {
  form: 'Contact form',
  reviews: 'Reviews widget',
  blog: 'Blog posts',
  quote: 'Instant quote button',
  map: 'Service-area map',
  gallery: 'Photo gallery',
  image: 'Image',
};

export function LockedComponentPanel({ ctx }: { ctx: PanelContext }) {
  const { selection, bridge, options, markDirty, updateComponentConfig, addRemovedMarkers, clearSelection } = ctx;
  const kind = selection.meta.lockedKind || 'component';

  return (
    <Box>
      <PanelTitle>{LOCKED_LABELS[kind] || 'Embedded component'}</PanelTitle>
      <Typography level="body-sm" sx={{ mb: 1 }}>
        This block is rendered live on your published site — its content is managed for you and can't
        be edited here. The Preview tab shows exactly how it looks.
      </Typography>
      {kind === 'form' && (options?.forms?.length ?? 0) > 0 && (
        <FormControl sx={{ mb: 1 }}>
          <FormLabel>Which form</FormLabel>
          <Select
            placeholder="Choose a form"
            onChange={(_, value) => {
              if (value) {
                updateComponentConfig({ form_embed_id: value });
                markDirty(selection.fieldKey, 'Switched the contact form');
              }
            }}
          >
            {options!.forms.map((form) => (
              <Option key={form.embed_id} value={form.embed_id}>
                {form.name}
              </Option>
            ))}
          </Select>
        </FormControl>
      )}
      {kind === 'reviews' && (options?.review_widgets?.length ?? 0) > 0 && (
        <FormControl sx={{ mb: 1 }}>
          <FormLabel>Which review widget</FormLabel>
          <Select
            placeholder="Choose a widget"
            onChange={(_, value) => {
              if (value) {
                updateComponentConfig({ review_widget_id: value });
                markDirty(selection.fieldKey, 'Switched the reviews widget');
              }
            }}
          >
            {options!.review_widgets.map((widget) => (
              <Option key={widget.uuid_public} value={widget.uuid_public}>
                {widget.name}
              </Option>
            ))}
          </Select>
        </FormControl>
      )}
      {selection.marker && selection.fieldKey && (
        <Button
          size="sm"
          color="danger"
          variant="outlined"
          startDecorator={<DeleteOutlineIcon />}
          onClick={() => {
            if (!window.confirm('Remove this component from the page? You can add it back later with an AI edit.')) {
              return;
            }
            addRemovedMarkers(bridge.deleteSelected());
            markDirty(selection.fieldKey, `Removed the ${LOCKED_LABELS[kind]?.toLowerCase() || 'component'}`);
            clearSelection();
          }}
        >
          Remove from page
        </Button>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------

export function TemplatePanel({ region }: { region?: EditorRegion }) {
  return (
    <Box>
      <PanelTitle>Page layout</PanelTitle>
      <Alert color="neutral" size="sm" startDecorator={<LockIcon />} sx={{ mb: 1 }}>
        {region?.reason ||
          "This block is part of the page's layout design, so it can't be edited by clicking."}
      </Alert>
      <Typography level="body-sm">
        You can still change it with the <strong>Edits</strong> tab in the site manager — describe
        what you want, e.g. "change the years in business to 10+ in the stats bar".
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------

export function ChromePanel({ projectId, region }: { projectId: string; region?: EditorRegion }) {
  return (
    <Box>
      <PanelTitle>Header / footer</PanelTitle>
      <Alert color="neutral" size="sm" startDecorator={<LockIcon />} sx={{ mb: 1 }}>
        {region?.reason ||
          "The header, navigation and footer are shared by every page, and this part can't be edited by clicking."}
      </Alert>
      <Stack spacing={0.5}>
        <Button
          size="sm"
          variant="outlined"
          component="a"
          href={`/marketing/website-builder/${projectId}/manage`}
        >
          Edit in Shared Components
        </Button>
        <Typography level="body-xs">
          …or type it into the AI edit box ("add our new tagline to the footer") — it applies to all
          pages at once.
        </Typography>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------

export function PagePanel({
  title,
  publishedAt,
  updatedAt,
  onTitleChange,
}: {
  title: string;
  publishedAt: string | null;
  updatedAt: string;
  onTitleChange: (title: string) => void;
}) {
  const [value, setValue] = useState(title);
  useEffect(() => setValue(title), [title]);
  const unpublished = !publishedAt || new Date(updatedAt) > new Date(publishedAt);

  return (
    <Box>
      <PanelTitle>Page</PanelTitle>
      <FormControl sx={{ mb: 1 }}>
        <FormLabel>Page title</FormLabel>
        <Input value={value} onChange={(e) => setValue(e.target.value)} onBlur={() => onTitleChange(value)} />
      </FormControl>
      <Chip size="sm" color={unpublished ? 'warning' : 'success'} variant="soft">
        {unpublished ? 'Has unpublished changes' : 'Published'}
      </Chip>
      <Typography level="body-xs" sx={{ mt: 1 }}>
        Click anything on the page to edit it. SEO settings live in the manager's SEO tab.
      </Typography>
    </Box>
  );
}
